import ResetPasswordPage from './pages/ResetPasswordPage';
import { useState } from 'react';
import { Routes, Route, Link, NavLink, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import Footer from './components/Footer';
import FeedbackForm from './components/FeedbackForm';
import ActiveTimerBar from './components/ActiveTimerBar';
import { supabase } from './lib/supabase';
import { CompanyProvider, useCompany } from './contexts/CompanyContext';

// Pagina imports
import DashboardPage from './pages/Dashboard';
import TimeEntryPage from './pages/TimeEntry';
import CostEntry from './pages/CostEntry';
import Reports from './pages/Reports';
import Auth from './pages/Auth';
import Kilometers from './pages/Kilometers';
import ProjectManagement from './pages/ProjectManagement';
import EmployeeManagement from './pages/EmployeeManagement';
import QuickStartPanel from './components/QuickStartPanel';
import DraftReviewModal from './components/DraftReviewModal';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useCompany();

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Laden...</div>;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

export default function AppWrapper() {
  return (
    <CompanyProvider>
      <App />
    </CompanyProvider>
  );
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showDraftReview, setShowDraftReview] = useState(false);
  const { user, companyId } = useCompany();

// Fetch current employee ID
const { data: currentEmployee } = useQuery({
  queryKey: ['currentEmployee', user?.id],
  queryFn: async () => {
    if (!user?.id) return null;
    
    const { data, error } = await supabase
      .from('employees')
      .select('id')
      .eq('user_id', user.id)
      .single();
    
    if (error) {
      console.error('Error fetching employee:', error);
      return null;
    }
    
    return data;
  },
  enabled: !!user?.id,
});
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/auth';
  };

  const handleTimerBarStop = async (data: {
  projectId: string;
  activityId: string;
  description: string;
  elapsedSeconds: number;
  totalBreakDuration: number;
}) => {
  try {
    // Get company_id
    const { data: companyData, error: companyError } = await supabase.rpc('get_current_user_company_id');
    if (companyError || !companyData) {
      toast.error('Geen bedrijf gevonden');
      return;
    }

    // Get user
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      toast.error('Niet ingelogd');
      return;
    }

    // Get employee_id
    const { data: employeeData, error: employeeError } = await supabase
      .from('employees')
      .select('id')
      .eq('user_id', userData.user.id)
      .maybeSingle();

    if (employeeError || !employeeData) {
      toast.error('Geen medewerker gevonden');
      return;
    }

    // Calculate times
    const now = Date.now();
    const startTime = now - (data.elapsedSeconds * 1000);
    const breakDurationMinutes = Math.floor(data.totalBreakDuration / 60);
    
    const formatTime = (timestamp: number) => {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('nl-NL', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const startTimeStr = formatTime(startTime);
    const endTimeStr = formatTime(now);
    const dateStr = new Date().toISOString().split('T')[0];

    const timeEntryData = {
      date: dateStr,
      start_time: startTimeStr,
      end_time: endTimeStr,
      break_duration: breakDurationMinutes,
      project_id: data.projectId,
      employee_id: employeeData.id,
      company_id: companyData,
      activity_id: data.activityId,
      notes: data.description || null,
      cost_category_id: null,
      hourly_rate: null,
      status: 'draft',
    };

    const { error: insertError } = await supabase
      .from('time_entries')
      .insert([timeEntryData])
      .select();

    if (insertError) {
      toast.error('Database fout: ' + insertError.message);
      return;
    }

    // Invalidate cache and force refetch active queries
    await queryClient.invalidateQueries({ queryKey: ['companyData'] });
    await queryClient.refetchQueries({ 
      queryKey: ['companyData'],
      type: 'active'
    });

    toast.success('Time entry opgeslagen!');
  } catch (error: any) {
    toast.error('Onverwachte fout: ' + (error.message || 'Onbekend'));
  }
};

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster />
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                TimeTracker
              </div>
              {/* Desktop Menu - 🆕 NEDERLANDSE VERSIE */}
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <NavLink to="/dashboard" className={({ isActive }) => `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>
                  Dashboard
                </NavLink>
                <NavLink to="/time-entry" className={({ isActive }) => `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>
                  Uren
                </NavLink>
                <NavLink to="/cost-entry" className={({ isActive }) => `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>
                  Kosten
                </NavLink>
                <NavLink to="/kilometers" className={({ isActive }) => `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>
                  Kilometers
                </NavLink>
                <NavLink to="/reports" className={({ isActive }) => `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>
                  Rapporten
                </NavLink>
                <NavLink to="/projects" className={({ isActive }) => `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>
                  Projecten
                </NavLink>
                <NavLink to="/employees" className={({ isActive }) => `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive ? 'border-indigo-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300'}`}>
                  Medewerkers
                </NavLink>
              </div>
            </div>
            <div className="flex items-center">
              {user ? (
                <>
                  <button
                    onClick={() => setShowDraftReview(true)}
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    📋 Review Drafts
                  </button>
                  <button 
                    onClick={() => setShowFeedback(true)}
                    className="hidden sm:block mr-3 px-3 py-2 rounded-md text-sm font-medium text-blue-600 hover:bg-blue-50"
                  >
                    Feedback
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="hidden sm:block px-3 py-2 rounded-md text-sm font-medium text-white bg-red-600"
                  >
                    Uitloggen
                  </button>
                </>
              ) : (
                <Link to="/auth" className="px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600">
                  Inloggen
                </Link>
              )}
              {/* Hamburger Menu Button */}
              {user && (
                <div className="sm:hidden flex items-center">
                  <button onClick={() => setIsMenuOpen(!isMenuOpen)} type="button" className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500" aria-controls="mobile-menu" aria-expanded="false">
                    <span className="sr-only">Open hoofdmenu</span>
                    {isMenuOpen ? (
                      <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    ) : (
                      <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Mobile menu - 🆕 NEDERLANDSE VERSIE */}
        {isMenuOpen && user && (
          <div className="sm:hidden" id="mobile-menu">
            <div className="pt-2 pb-3 space-y-1">
              <NavLink to="/dashboard" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}>
                Dashboard
              </NavLink>
              <NavLink to="/time-entry" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}>
                Uren
              </NavLink>
              <NavLink to="/cost-entry" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}>
                Kosten
              </NavLink>
              <NavLink to="/kilometers" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}>
                Kilometers
              </NavLink>
              <NavLink to="/reports" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}>
                Rapporten
              </NavLink>
              <NavLink to="/projects" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}>
                Projecten
              </NavLink>
              <NavLink to="/employees" onClick={() => setIsMenuOpen(false)} className={({ isActive }) => `block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}>
                Medewerkers
              </NavLink>
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  setShowDraftReview(true);
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                📋 Review Drafts
              </button>
              <button onClick={() => { setShowFeedback(true); setIsMenuOpen(false); }} className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-blue-600 hover:bg-blue-50 hover:border-blue-300">
                Feedback
              </button>
              <button onClick={handleLogout} className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-red-600 hover:bg-red-50 hover:border-red-300">
                Uitloggen
              </button>
            </div>
          </div>
        )}
      </nav>
      <main className="py-10">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/time-entry" element={<ProtectedRoute><TimeEntryPage /></ProtectedRoute>} />
            <Route path="/cost-entry" element={<ProtectedRoute><CostEntry /></ProtectedRoute>} />
            <Route path="/kilometers" element={<ProtectedRoute><Kilometers /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><ProjectManagement /></ProtectedRoute>} />
            <Route path="/employees" element={<ProtectedRoute><EmployeeManagement /></ProtectedRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Routes>
        </div>
      </main>
      <Footer />
      <FeedbackForm isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
      <ActiveTimerBar onStop={handleTimerBarStop} />
      {user && companyId && <QuickStartPanel companyId={companyId} />}
      {user && currentEmployee && (
  <DraftReviewModal
    isOpen={showDraftReview}
    onClose={() => setShowDraftReview(false)}
    employeeId={currentEmployee.id}
  />
)}
    </div>
  );
}